import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useClient } from '../../contexts/ClientContext';
import { api } from '../../utils/api';
import { dataCache } from '../../utils/dataCache';

function getDisplayDomain(client: { clientName?: string; primaryDomain?: string; domains?: string[] }): string {
  return client.primaryDomain || client.domains?.[0] || client.clientName || '';
}

const ClientSwitcher: React.FC<{ isAdmin?: boolean; userEmail?: string | null }> = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const { clients, selectedClient, setSelectedClient, refreshClients, loading, locationRestricted } = useClient();
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientDomain, setNewClientDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClientChange = (event: any) => {
    const clientName = event.target.value as string;
    if (clientName === '__create_new__') {
      setNewClientDialogOpen(true);
      return;
    }
    const client = clients.find(c => c.clientName === clientName);
    if (client) {
      setSelectedClient(client);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim() || !newClientDomain.trim()) {
      setError('Please provide both name and domain');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const normalizedDomain = newClientDomain.trim()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0];

      // Note: We removed the frontend pre-check. The backend now handles domain
      // uniqueness checking only within the current user's clients.
      // Different users can have the same domain (data is scoped by user_id).
      const response = await api.post('/api/clients', {
        name: newClientName.trim(),
        domain: normalizedDomain,
        businessSummary: '',
        topics: [],
        competitors: []
      });

      const body = (response as any).data !== undefined ? (response as any).data : response;
      if (body.success) {
        const createdClientName = body.clientName || body.name?.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const normDomain = (d: string) => (d || '').toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').trim();
        const domainToMatch = normDomain(newClientDomain);

        // Refresh clients list and select the one we just added (so user lands on frannet, not netsertive)
        const updatedClients = await refreshClients(createdClientName);
        const newClient = updatedClients.find((c: { clientName: string }) => c.clientName === createdClientName) ||
          updatedClients.find((c: { primaryDomain?: string; domains?: string[] }) =>
            normDomain(c.primaryDomain || '') === domainToMatch ||
            (c.domains || []).some((d: string) => normDomain(d) === domainToMatch)
          );
        const clientToSelect = newClient || {
          clientName: createdClientName,
          name: body.name || newClientName.trim(),
          primaryDomain: newClientDomain.trim(),
          promptCount: 0,
          domains: [newClientDomain.trim()],
          competitors: [],
          businessSummary: ''
        };
        // Force context to update synchronously so all screens use the new client (not just the dropdown)
        flushSync(() => {
          setSelectedClient(clientToSelect);
        });
        // Clear cache for the new client so AEO/recommendations etc. fetch fresh data
        dataCache.clearClient(clientToSelect.clientName);
        
        setNewClientDialogOpen(false);
        setNewClientName('');
        setNewClientDomain('');
        setError(null);

        if (body.alreadyInAccount) {
          return;
        }
        
        navigate('/prompts', { state: { addedClient: clientToSelect } });
        setTimeout(async () => {
          try {
            console.log(`🔄 Generating prompts for client: ${createdClientName}`);
            const result = await api.post(`/api/generate-prompts/${encodeURIComponent(createdClientName)}`);
            console.log(`✅ Prompts generated:`, result);
            sessionStorage.setItem('promptGenerationTriggered', 'true');
            sessionStorage.setItem('newClientName', body.name || newClientName.trim());
            sessionStorage.setItem('bypassPromptCache', 'true');
            window.dispatchEvent(new Event('clientCreated'));
          } catch (err) {
            console.error('Error generating prompts:', err);
            sessionStorage.setItem('promptGenerationTriggered', 'true');
            sessionStorage.setItem('newClientName', body.name || newClientName.trim());
          }
        }, 500);
      }
    } catch (err: any) {
      // Show backend error when domain already exists (do not auto-switch; user must choose)
      if (err.message && (err.message.includes('already belongs to') || err.message.includes('already exists'))) {
        setError(err.message);
      } else {
        const fallbackMessage = err.message || err.error || 'Failed to create client';
        setError(
          fallbackMessage === 'Validation failed'
            ? 'Please enter a valid domain (e.g., frannet.com).'
            : fallbackMessage
        );
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  if (locationRestricted && selectedClient) {
    return (
      <Box sx={{ minWidth: { xs: 140, sm: 180 }, maxWidth: { xs: '100%', sm: 220 } }}>
        <Typography variant="body2" noWrap sx={{ py: 0.75, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          {getDisplayDomain(selectedClient)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          Single-location account
        </Typography>
      </Box>
    );
  }

  // For non-admin users with access to only one client, render static text instead of a selector.
  if (!isAdmin && clients.length === 1 && selectedClient) {
    return (
      <Box sx={{ minWidth: { xs: 140, sm: 180 }, maxWidth: { xs: '100%', sm: 220 } }}>
        <Typography
          variant="body2"
          noWrap
          sx={{
            py: 0.75,
            px: 1.5,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            fontWeight: 500
          }}
        >
          {getDisplayDomain(selectedClient)}
        </Typography>
      </Box>
    );
  }

  if (clients.length === 0) {
    if (!isAdmin) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            No clients available
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setNewClientDialogOpen(true)}
          size="small"
        >
          Add First Client
        </Button>
        <Dialog open={newClientDialogOpen} onClose={() => setNewClientDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Client</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              label="Client Name"
              fullWidth
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              margin="normal"
              placeholder="e.g., FranNet"
            />
            <TextField
              label="Domain"
              fullWidth
              value={newClientDomain}
              onChange={(e) => setNewClientDomain(e.target.value)}
              margin="normal"
              placeholder="e.g., frannet.com"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewClientDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateClient} variant="contained" disabled={creating}>
              {creating ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: { xs: 180, sm: 220 }, maxWidth: { xs: '100%', sm: 220 } }}>
      <FormControl fullWidth size="small" variant="outlined">
        <Select
          value={selectedClient?.clientName || ''}
          onChange={handleClientChange}
          displayEmpty
          sx={{ bgcolor: 'background.paper', minWidth: 0 }}
          renderValue={(value) => {
            const client = clients.find(c => c.clientName === value);
            if (!client) return '';
            return (
              <Typography variant="body2" noWrap>
                {getDisplayDomain(client)}
              </Typography>
            );
          }}
        >
          {clients.map((client) => (
            <MenuItem key={client.clientName} value={client.clientName}>
              <Typography variant="body2">{getDisplayDomain(client)}</Typography>
            </MenuItem>
          ))}
          {isAdmin && !locationRestricted && (
            <MenuItem value="__create_new__" sx={{ borderTop: 1, borderColor: 'divider', mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon fontSize="small" />
                <Typography variant="body2">Add New Client</Typography>
              </Box>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      <Dialog open={newClientDialogOpen} onClose={() => setNewClientDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Client</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            label="Client Name"
            fullWidth
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            margin="normal"
            placeholder="e.g., FranNet"
          />
          <TextField
            label="Domain"
            fullWidth
            value={newClientDomain}
            onChange={(e) => setNewClientDomain(e.target.value)}
            margin="normal"
            placeholder="e.g., frannet.com"
            helperText="Enter the main domain to track (e.g., frannet.com)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewClientDialogOpen(false);
            setError(null);
            setNewClientName('');
            setNewClientDomain('');
          }}>Cancel</Button>
          <Button onClick={handleCreateClient} variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientSwitcher;

